import { supabase } from '../lib/supabaseClient';

const API_BASE = '/api/staff';

// Helper to get auth headers
const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session) throw new Error('Not authenticated');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${session.access_token}`
  };
};

// ==================== TIME CLOCK ====================
export interface TimeEntry {
  id?: string;
  staff_id: string;
  user_id?: string;
  clock_in: string;
  clock_out?: string;
  clock_in_location?: string;
  clock_out_location?: string;
  clock_in_notes?: string;
  clock_out_notes?: string;
  break_duration_minutes?: number;
  total_hours?: number;
  status?: 'pending' | 'approved' | 'rejected' | 'disputed';
  created_at?: string;
}

export const clockIn = async (staff_id: string, location?: string, notes?: string) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/time-clock/in`, {
    method: 'POST',
    headers,
    body: JSON.stringify({ staff_id, location, notes })
  });
  if (!response.ok) throw new Error('Failed to clock in');
  return response.json();
};

export const clockOut = async (id: string, location?: string, notes?: string, break_duration_minutes?: number) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/time-clock/out/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify({ location, notes, break_duration_minutes })
  });
  if (!response.ok) throw new Error('Failed to clock out');
  return response.json();
};

export const getTimeEntries = async (filters?: { staff_id?: string; start_date?: string; end_date?: string }) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters as any);
  const response = await fetch(`${API_BASE}/time-entries?${params}`, { headers });
  if (!response.ok) throw new Error('Failed to fetch time entries');
  return response.json();
};

// ==================== DOCUMENTS ====================
export interface StaffDocument {
  id?: string;
  staff_id?: string;
  document_type: 'contract' | 'certification' | 'policy' | 'review' | 'identification' | 'tax_form' | 'other';
  title: string;
  file_name: string;
  file_url: string;
  file_size?: number;
  mime_type?: string;
  expiry_date?: string;
  is_confidential?: boolean;
  notes?: string;
  created_at?: string;
}

export const uploadDocument = async (doc: StaffDocument) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/documents`, {
    method: 'POST',
    headers,
    body: JSON.stringify(doc)
  });
  if (!response.ok) throw new Error('Failed to upload document');
  return response.json();
};

export const getDocuments = async (filters?: { staff_id?: string; document_type?: string }) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters as any);
  const response = await fetch(`${API_BASE}/documents?${params}`, { headers });
  if (!response.ok) throw new Error('Failed to fetch documents');
  return response.json();
};

export const deleteDocument = async (id: string) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/documents/${id}`, {
    method: 'DELETE',
    headers
  });
  if (!response.ok) throw new Error('Failed to delete document');
  return response.json();
};

// ==================== PERFORMANCE REVIEWS ====================
export interface PerformanceReview {
  id?: string;
  staff_id: string;
  reviewer_id?: string;
  review_period_start: string;
  review_period_end: string;
  review_type?: 'annual' | 'probation' | 'mid_year' | 'project' | '360';
  overall_rating?: number;
  strengths?: string;
  areas_for_improvement?: string;
  goals_achieved?: string;
  new_goals?: string;
  training_needs?: string;
  comments?: string;
  status?: 'draft' | 'submitted' | 'completed' | 'archived';
  created_at?: string;
}

export const createReview = async (review: PerformanceReview) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/reviews`, {
    method: 'POST',
    headers,
    body: JSON.stringify(review)
  });
  if (!response.ok) throw new Error('Failed to create review');
  return response.json();
};

export const getReviews = async (staff_id?: string) => {
  const headers = await getAuthHeaders();
  const params = staff_id ? `?staff_id=${staff_id}` : '';
  const response = await fetch(`${API_BASE}/reviews${params}`, { headers });
  if (!response.ok) throw new Error('Failed to fetch reviews');
  return response.json();
};

export const updateReview = async (id: string, updates: Partial<PerformanceReview>) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/reviews/${id}`, {
    method: 'PUT',
    headers,
    body: JSON.stringify(updates)
  });
  if (!response.ok) throw new Error('Failed to update review');
  return response.json();
};

// ==================== SKILLS ====================
export interface Skill {
  id?: string;
  staff_id: string;
  skill_name: string;
  skill_category?: 'technical' | 'soft' | 'language' | 'certification' | 'license';
  proficiency_level?: 'beginner' | 'intermediate' | 'advanced' | 'expert';
  acquired_date?: string;
  expiry_date?: string;
  certification_number?: string;
  issuing_organization?: string;
  verification_url?: string;
  notes?: string;
}

export const addSkill = async (skill: Skill) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/skills`, {
    method: 'POST',
    headers,
    body: JSON.stringify(skill)
  });
  if (!response.ok) throw new Error('Failed to add skill');
  return response.json();
};

export const getSkills = async (staff_id?: string) => {
  const headers = await getAuthHeaders();
  const params = staff_id ? `?staff_id=${staff_id}` : '';
  const response = await fetch(`${API_BASE}/skills${params}`, { headers });
  if (!response.ok) throw new Error('Failed to fetch skills');
  return response.json();
};

export const getExpiringCertifications = async (days: number = 30) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/skills/expiring?days=${days}`, { headers });
  if (!response.ok) throw new Error('Failed to fetch expiring certifications');
  return response.json();
};

// ==================== ANNOUNCEMENTS ====================
export interface Announcement {
  id?: string;
  title: string;
  message: string;
  priority?: 'low' | 'normal' | 'high' | 'urgent';
  target_audience?: 'all' | 'department' | 'specific_staff' | 'managers';
  target_department?: string;
  target_staff_ids?: string[];
  is_pinned?: boolean;
  expires_at?: string;
  created_at?: string;
}

export const createAnnouncement = async (announcement: Announcement) => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/announcements`, {
    method: 'POST',
    headers,
    body: JSON.stringify(announcement)
  });
  if (!response.ok) throw new Error('Failed to create announcement');
  return response.json();
};

export const getAnnouncements = async () => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/announcements`, { headers });
  if (!response.ok) throw new Error('Failed to fetch announcements');
  return response.json();
};

// ==================== ANALYTICS ====================
export interface StaffAnalytics {
  total_staff: number;
  pending_holidays: number;
  today_attendance: number;
  overdue_compliance: number;
}

export const getAnalyticsOverview = async (): Promise<{ data: StaffAnalytics }> => {
  const headers = await getAuthHeaders();
  const response = await fetch(`${API_BASE}/analytics/overview`, { headers });
  if (!response.ok) throw new Error('Failed to fetch analytics');
  return response.json();
};

export const getAbsenceReport = async (filters?: { start_date?: string; end_date?: string }) => {
  const headers = await getAuthHeaders();
  const params = new URLSearchParams(filters as any);
  const response = await fetch(`${API_BASE}/analytics/absences?${params}`, { headers });
  if (!response.ok) throw new Error('Failed to fetch absence report');
  return response.json();
};

const staffExtendedService = {
  clockIn,
  clockOut,
  getTimeEntries,
  uploadDocument,
  getDocuments,
  deleteDocument,
  createReview,
  getReviews,
  updateReview,
  addSkill,
  getSkills,
  getExpiringCertifications,
  createAnnouncement,
  getAnnouncements,
  getAnalyticsOverview,
  getAbsenceReport
};

export default staffExtendedService;
